import { useMemo, useState } from 'react';
import {
  AlertTriangle,
  HardDrive,
  RefreshCw,
  Trash2,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { api } from '../../services/api';
import toast from '../Toast';
import type { StorageCleanupItem, StorageCleanupSummary } from './types';
import { formatBeijingDateTime } from '../../utils/time';

const PAGE_SIZE = 50;

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

export default function StorageCleanupTab() {
  const [items, setItems] = useState<StorageCleanupItem[]>([]);
  const [summary, setSummary] = useState<StorageCleanupSummary | null>(null);
  const [scanning, setScanning] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [selectedPaths, setSelectedPaths] = useState<Set<string>>(new Set());
  const [page, setPage] = useState(1);

  const totalPages = Math.max(1, Math.ceil(items.length / PAGE_SIZE));

  const pagedItems = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return items.slice(start, start + PAGE_SIZE);
  }, [items, page]);

  const visibleSelectedCount = pagedItems.filter((item) => selectedPaths.has(item.filePath)).length;
  const allVisibleSelected = pagedItems.length > 0 && visibleSelectedCount === pagedItems.length;

  const runScan = async () => {
    setScanning(true);
    try {
      const result = await api.scanAdminStorageOrphans();
      setItems(result.items);
      setSummary(result.summary);
      setSelectedPaths(new Set());
      setPage(1);
      toast.success(`扫描完成，共发现 ${result.summary.totalFiles} 个候选文件`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : '扫描失败');
    } finally {
      setScanning(false);
    }
  };

  const toggleSelect = (filePath: string) => {
    setSelectedPaths((prev) => {
      const next = new Set(prev);
      if (next.has(filePath)) {
        next.delete(filePath);
      } else {
        next.add(filePath);
      }
      return next;
    });
  };

  const toggleSelectCurrentPage = () => {
    setSelectedPaths((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) {
        for (const item of pagedItems) {
          next.delete(item.filePath);
        }
      } else {
        for (const item of pagedItems) {
          next.add(item.filePath);
        }
      }
      return next;
    });
  };

  const handleDelete = async () => {
    if (selectedPaths.size === 0) {
      toast.error('请先选择要删除的文件');
      return;
    }

    if (!window.confirm(`确定删除选中的 ${selectedPaths.size} 个文件吗？删除前会再次校验是否仍符合清理条件。`)) {
      return;
    }

    setDeleting(true);
    try {
      const result = await api.deleteAdminStorageOrphans(Array.from(selectedPaths));
      toast.success(result.message);
      await runScan();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : '删除失败');
    } finally {
      setDeleting(false);
    }
  };

  const pageNumbers = Array.from({ length: totalPages }, (_, index) => index + 1).filter((value) => {
    if (totalPages <= 7) return true;
    if (value <= 3 || value > totalPages - 3) return true;
    return Math.abs(value - page) <= 1;
  });

  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
        <div className="flex items-start gap-3">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
          <div>
              <p className="font-medium">仅处理 MinIO 中已过保留期的孤立媒体对象</p>
            <p className="mt-1">候选对象必须位于 `images/` 或 `videos/`，未被媒体账本或任务记录引用，并且最后修改时间已超过 7 天。</p>
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-light-border bg-white p-4 shadow-sm">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h3 className="text-base font-semibold text-light-text">孤立文件扫描</h3>
            <p className="mt-1 text-sm text-light-muted">
              默认只扫描，不会自动删除任何文件。
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={runScan}
              disabled={scanning || deleting}
              className="inline-flex items-center gap-2 rounded-xl bg-accent-blue px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-accent-blue/90 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <RefreshCw className={`h-4 w-4 ${scanning ? 'animate-spin' : ''}`} />
              {summary ? '重新扫描' : '开始扫描'}
            </button>
            <button
              onClick={handleDelete}
              disabled={selectedPaths.size === 0 || scanning || deleting}
              className="inline-flex items-center gap-2 rounded-xl bg-red-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-red-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <Trash2 className="h-4 w-4" />
              删除已选
            </button>
          </div>
        </div>
      </div>

      {summary && (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
          <SummaryCard title="候选总数" value={`${summary.totalFiles} 个`} detail={formatBytes(summary.totalBytes)} />
          <SummaryCard title="孤立 MinIO 对象" value={`${summary.legacyPendingCount} 个`} detail={formatBytes(summary.legacyPendingBytes)} />
          <SummaryCard title="保留期" value="7 天" detail="到期后才可成为候选" />
          <SummaryCard title="扫描时间" value={formatBeijingDateTime(summary.scannedAt)} detail="手动触发" />
        </div>
      )}

      {summary && (
        <div className="rounded-2xl border border-light-border bg-white shadow-sm">
          <div className="flex flex-col gap-3 border-b border-light-border px-4 py-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-center gap-3">
              <HardDrive className="h-5 w-5 text-light-muted" />
              <div>
                <h3 className="text-base font-semibold text-light-text">扫描结果</h3>
                <p className="text-sm text-light-muted">
                  共 {items.length} 个候选文件，当前已选 {selectedPaths.size} 个
                </p>
              </div>
            </div>
            {pagedItems.length > 0 && (
              <button
                onClick={toggleSelectCurrentPage}
                className="rounded-lg bg-light-hover px-3 py-1.5 text-sm text-light-text transition-colors hover:bg-light-border"
              >
                {allVisibleSelected ? '取消本页全选' : '全选本页'}
              </button>
            )}
          </div>

          {items.length === 0 ? (
            <div className="px-4 py-14 text-center text-sm text-light-muted">
              {scanning ? '扫描中...' : '当前未发现符合条件的候选文件'}
            </div>
          ) : (
            <>
              <div className="hidden lg:block overflow-x-auto">
                <table className="w-full min-w-[980px] text-sm">
                  <thead className="bg-gray-50 text-left text-gray-600">
                    <tr>
                      <th className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={allVisibleSelected}
                          onChange={toggleSelectCurrentPage}
                          className="h-4 w-4 rounded border-light-border text-accent-blue focus:ring-accent-blue"
                        />
                      </th>
                      <th className="px-4 py-3 font-medium">类型</th>
                      <th className="px-4 py-3 font-medium">文件路径</th>
                      <th className="px-4 py-3 font-medium">用户</th>
                      <th className="px-4 py-3 font-medium">大小</th>
                      <th className="px-4 py-3 font-medium">后缀</th>
                      <th className="px-4 py-3 font-medium">最后修改</th>
                      <th className="px-4 py-3 font-medium">原因</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {pagedItems.map((item) => (
                      <tr key={item.filePath} className="hover:bg-gray-50/70">
                        <td className="px-4 py-3 align-top">
                          <input
                            type="checkbox"
                            checked={selectedPaths.has(item.filePath)}
                            onChange={() => toggleSelect(item.filePath)}
                            className="mt-1 h-4 w-4 rounded border-light-border text-accent-blue focus:ring-accent-blue"
                          />
                        </td>
                        <td className="px-4 py-3 align-top">
                          <CategoryBadge item={item} />
                        </td>
                        <td className="px-4 py-3 align-top">
                          <div className="max-w-[420px] break-all font-mono text-xs text-light-text">
                            {item.filePath}
                          </div>
                        </td>
                        <td className="px-4 py-3 align-top text-light-text">
                          {item.userId || '-'}
                        </td>
                        <td className="px-4 py-3 align-top text-light-text">
                          {formatBytes(item.size)}
                        </td>
                        <td className="px-4 py-3 align-top text-light-text">
                          {item.extension || '-'}
                        </td>
                        <td className="px-4 py-3 align-top text-light-text">
                          {formatModifiedAt(item.modifiedAt)}
                        </td>
                        <td className="px-4 py-3 align-top text-light-muted">
                          {item.reason}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="divide-y divide-light-border lg:hidden">
                {pagedItems.map((item) => (
                  <div key={item.filePath} className="p-4 space-y-3">
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        checked={selectedPaths.has(item.filePath)}
                        onChange={() => toggleSelect(item.filePath)}
                        className="mt-1 h-4 w-4 rounded border-light-border text-accent-blue focus:ring-accent-blue"
                      />
                      <div className="min-w-0 flex-1 space-y-3">
                        <div className="flex items-start justify-between gap-3">
                          <CategoryBadge item={item} />
                          <span className="text-xs text-light-muted shrink-0">{formatBytes(item.size)}</span>
                        </div>
                        <div className="break-all rounded-xl bg-light-hover p-3 font-mono text-xs text-light-text">
                          {item.filePath}
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <InfoTile label="用户" value={item.userId || '-'} />
                          <InfoTile label="后缀" value={item.extension || '-'} />
                        </div>
                        <InfoTile label="最后修改" value={formatModifiedAt(item.modifiedAt)} />
                        <InfoTile label="原因" value={item.reason} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {totalPages > 1 && (
                <div className="flex flex-col gap-3 border-t border-light-border px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="text-sm text-light-muted">
                    共 {items.length} 条，第 {page} / {totalPages} 页
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                      disabled={page <= 1}
                      className="rounded-lg p-2 transition-colors hover:bg-light-hover disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      <ChevronLeft className="h-4 w-4 text-light-text" />
                    </button>
                    {pageNumbers.map((pageNumber, index) => {
                      const previous = pageNumbers[index - 1];
                      const showEllipsis = previous && pageNumber - previous > 1;
                      return (
                        <div key={pageNumber} className="flex items-center gap-1">
                          {showEllipsis && <span className="px-2 text-sm text-light-muted">...</span>}
                          <button
                            onClick={() => setPage(pageNumber)}
                            className={`min-w-8 rounded-lg px-2 py-1 text-sm transition-colors ${
                              pageNumber === page
                                ? 'bg-accent-blue text-white'
                                : 'text-light-text hover:bg-light-hover'
                            }`}
                          >
                            {pageNumber}
                          </button>
                        </div>
                      );
                    })}
                    <button
                      onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
                      disabled={page >= totalPages}
                      className="rounded-lg p-2 transition-colors hover:bg-light-hover disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      <ChevronRight className="h-4 w-4 text-light-text" />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function formatModifiedAt(value: string): string {
  return formatBeijingDateTime(value);
}

function CategoryBadge({ item }: { item: StorageCleanupItem }) {
  return <span className="inline-flex rounded-full bg-amber-100 px-2.5 py-1 text-xs font-medium text-amber-700">{item.categoryLabel}</span>;
}

function InfoTile({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl bg-light-hover p-3">
      <div className="text-xs text-light-muted">{label}</div>
      <div className="mt-1 break-all text-sm text-light-text">{value}</div>
    </div>
  );
}

function SummaryCard({
  title,
  value,
  detail,
}: {
  title: string;
  value: string;
  detail: string;
}) {
  return (
    <div className="rounded-2xl border border-light-border bg-white p-4 shadow-sm">
      <p className="text-sm text-light-muted">{title}</p>
      <p className="mt-2 text-xl font-semibold text-light-text">{value}</p>
      <p className="mt-1 text-sm text-light-muted">{detail}</p>
    </div>
  );
}
